import { useCallback } from 'react';
import { AxiosResponse } from 'axios';

import { useApi, useLocalStorage } from '.';

export const FAILED_ATTEMPTS_THRESHOLD = 1;
export const LOCAL_STORAGE_KEYS = {
  AUTH_DATA: 'ngStorage-authData',
  FAILED_ATTEMPTS: 'ngStorage-failedLoginCount',
  USER_ID: 'ngStorage-currentUid',
  draftRequests: 'draftRequests',
};

// TODO: Update redux for hook support and remove this
interface Param {
  dispatchAuth: () => void;
}

interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  expires: number;
  token: string;
  uid: string;
}

const useLogin = ({ dispatchAuth }: Param) => {
  const [{ error, loading: isLoading }, manualRequest] = useApi<LoginResponse>(
    {
      method: 'POST',
      url: '/auth/login',
    },
    { manual: true }
  );
  const [, setAuthData] = useLocalStorage<LoginResponse | null>(LOCAL_STORAGE_KEYS.AUTH_DATA, null);
  const [failedAttempts, setFailedAttempts, clearFailedAttempts] = useLocalStorage<number>(
    LOCAL_STORAGE_KEYS.FAILED_ATTEMPTS,
    0
  );
  const [, setUserId] = useLocalStorage<string>(LOCAL_STORAGE_KEYS.USER_ID, '');

  const handleSuccess = useCallback(
    (response: LoginResponse) => {
      setAuthData(response);
      setUserId(response.uid);
      clearFailedAttempts();
      dispatchAuth();
    },
    [clearFailedAttempts, dispatchAuth, setAuthData, setUserId]
  );

  const handleFailure = useCallback(() => {
    setFailedAttempts((attempts) => attempts + 1);
  }, [setFailedAttempts]);

  const login = useCallback<(param: LoginRequest) => Promise<void>>(
    ({ email, password }) =>
      manualRequest({ data: { email, password } }).then(
        (response: AxiosResponse<LoginResponse>) => handleSuccess(response.data),
        () => handleFailure()
      ),
    [manualRequest, handleSuccess, handleFailure]
  );

  return {
    error,
    isLoading,
    isOverFailureThreshold: failedAttempts >= FAILED_ATTEMPTS_THRESHOLD,
    login,
  };
};

export default useLogin;
